import pRetry from 'p-retry'
import type { Options as RetryOptions } from 'p-retry'

import type { ClientContext } from './types'

export type RequestOptions = RequestInit & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json?: Record<string, any> | any[]
  withAuth?: boolean
} & RetryOptions

export async function request<T>(
  context: ClientContext,
  path: string,
  {
    withAuth,
    headers,
    method = 'GET',
    retries = 3,
    maxRetryTime,
    ...options
  }: RequestOptions = {},
): Promise<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const localHeaders: Record<string, any> = {
    'Content-Type': 'application/json',
  }

  if (withAuth) {
    const token = await context.getToken?.()

    if (token == null) {
      throw Error('Missing user token')
    }

    localHeaders.Authorization = `Bearer ${token}`
  }

  const normalizedAPIPath = path.replace(/^\//, '')
  const url = `${context.baseUrl}/${normalizedAPIPath}`

  // https://github.com/sindresorhus/p-retry/blob/main/readme.md
  return pRetry(
    async () => {
      const response = await fetch(url, {
        method,
        referrerPolicy: 'no-referrer',
        headers: {
          ...localHeaders,
          ...headers,
        },
        body: JSON.stringify(options.json) ?? options.body,
        ...options,
      })

      // Abort retrying if the resource doesn't exist
      if (response.status === 404) {
        throw new pRetry.AbortError(response.statusText)
      }

      // Do not parse JSON response if it's 204 - No Content
      if (response.status === 204) {
        return response
      }

      return response.json()
    },
    {
      retries,
      maxRetryTime,
    },
  )
}
